import { useCallback, useEffect, useState } from 'react';
import { MdPersonAdd, MdSearch, MdGroups } from 'react-icons/md';
import { Drawer, DialogBody, DialogFooter } from '../ui/Dialog';
import { Badge, Button, Checkbox, EmptyState, FilterChip, SkeletonList } from '../ui';
import { groupMembers, importGroupMembers } from '../../api/addressBook';
import { messaggioErrore } from '../../utils/apiError';

/**
 * Chi c'è dentro un gruppo WhatsApp, per pescarne i contatti.
 * È l'unico motivo per cui un gruppo sta in rubrica: come bersaglio delle
 * automazioni funziona già dalla lista chat, senza passare di qui.
 */
export default function GroupMembersDrawer({ entry, open, onClose, onImported }) {
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');
  const [scelti, setScelti] = useState([]);
  const [query, setQuery] = useState('');
  const [conNome, setConNome] = useState(true);

  const carica = useCallback(async () => {
    if (!entry) return;
    setCaricamento(true); setErrore(''); setScelti([]); setQuery('');
    try {
      setDati(await groupMembers(entry.id));
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setCaricamento(false);
    }
  }, [entry]);

  useEffect(() => { if (open) carica(); else setDati(null); }, [open, carica]);

  if (!entry) return null;

  const q = query.trim().toLocaleLowerCase();
  const membri = (dati?.members || []).filter(m => (
    (!conNome || m.name || m.already)
    && (!q || `${m.name} ${m.phone} ${m.whatsapp_id}`.toLocaleLowerCase().includes(q))
  ));
  const conNomeTotali = (dati?.members || []).filter(m => m.name).length;
  const aggiungibili = membri.filter(m => !m.already);
  const selezionati = new Set(scelti);

  const aggiungi = async () => {
    setBusy(true); setErrore('');
    try {
      const esito = await importGroupMembers(entry.id, scelti);
      await carica();
      onImported?.(esito);
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} label={entry.name}
      description={dati ? `${dati.total} partecipanti, ${dati.new} non ancora in rubrica` : 'Gruppo WhatsApp'}>
      <DialogBody className="space-y-3">
        {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}
        {dati?.error && <p role="alert" className="rounded bg-warning-bg px-3 py-2 text-sm text-warning">{dati.error}</p>}

        {caricamento ? <SkeletonList rows={5} /> : !dati?.members?.length ? (
          <EmptyState icon={MdGroups} title="Nessun partecipante leggibile"
            hint="Apri il gruppo su WhatsApp sul telefono, poi riprova: i dati del gruppo vengono caricati da lì." />
        ) : (
          <>
            <p className="text-xs text-muted">
              La maggior parte dei partecipanti non ha un numero leggibile da WhatsApp. Puoi
              aggiungerli comunque: l&apos;identità basta per scrivergli.
            </p>
            <div className="relative">
              <MdSearch size={18} aria-hidden className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
              <input aria-label="Cerca fra i partecipanti" placeholder="Cerca nome o numero"
                value={query} onChange={e => setQuery(e.target.value)}
                className="w-full rounded border border-line bg-surface py-2 pl-8 pr-3 text-base md:text-sm" />
            </div>

            <FilterChip active={conNome} count={conNomeTotali} onClick={() => setConNome(!conNome)}>
              Solo con un nome
            </FilterChip>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-ink" aria-live="polite">
                {scelti.length > 0 ? `${scelti.length} selezionati` : `${aggiungibili.length} da aggiungere`}
              </p>
              <Button size="sm" disabled={!aggiungibili.length}
                onClick={() => setScelti([...new Set([...scelti, ...aggiungibili.map(m => m.whatsapp_id)])])}>
                Seleziona tutti
              </Button>
            </div>

            <ul className="overflow-hidden rounded border border-line">
              {membri.map(m => (
                <li key={m.whatsapp_id} className="border-b border-line px-2 py-1.5 last:border-0">
                  {m.already ? (
                    <p className="flex flex-wrap items-center gap-2 pl-7 text-sm text-muted">
                      {m.name || m.whatsapp_id.split('@')[0]}
                      <Badge tone="success">Già in rubrica</Badge>
                      {m.admin && <Badge>Amministratore</Badge>}
                    </p>
                  ) : (
                    <Checkbox
                      checked={selezionati.has(m.whatsapp_id)}
                      onChange={e => setScelti(e.target.checked
                        ? [...scelti, m.whatsapp_id]
                        : scelti.filter(x => x !== m.whatsapp_id))}
                      label={(
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{m.name || m.whatsapp_id.split('@')[0]}</span>
                          {m.phone
                            ? <span className="text-xs tabular-nums text-muted">{m.phone}</span>
                            : <Badge tone="warning">Senza numero</Badge>}
                          {m.admin && <Badge>Amministratore</Badge>}
                        </span>
                      )}
                    />
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Chiudi</Button>
        <Button variant="primary" icon={MdPersonAdd} loading={busy} disabled={!scelti.length}
          onClick={aggiungi}>
          Aggiungi {scelti.length || ''} alla rubrica
        </Button>
      </DialogFooter>
    </Drawer>
  );
}
